import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, fieldInputCls } from "@/components/Field";
import { TagInput } from "@/components/TagInput";
import { useSettings } from "@/context/SettingsContext";
import { driverName } from "@/lib/drivers";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", track: "", event_type: "FIA", country: "", categories: [], drivers: [], start_date: "", end_date: "", notes: "" };

export const EventFormDialog = ({ open, onOpenChange, event, tracks = [], onSaved }) => {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ drivers: [], categories: [] });
  const [registry, setRegistry] = useState([]);
  const { settings, visibleTypes } = useSettings();
  useEffect(() => {
    if (open) {
      setForm(event ? { ...EMPTY, ...event } : { ...EMPTY, event_type: visibleTypes[0] || "OTHER" });
      api.get("/events/meta").then((r) => setMeta(r.data)).catch(() => {});
      api.get("/drivers").then((r) => setRegistry(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event]);
  const typeOptions = visibleTypes.includes(form.event_type) ? visibleTypes : [...visibleTypes, form.event_type];
  const suggestedDrivers = registry.map(driverName).filter((n) => n && !form.drivers.includes(n));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      ["id", "user_id", "created_at", "session_count", "category"].forEach((k) => delete payload[k]);
      const { data } = event ? await api.put(`/events/${event.id}`, payload) : await api.post("/events", payload);
      toast.success(t("saved"));
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-form-dialog" className="bg-[#12141C] border-[#282D3D] rounded-sm max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-2xl tracking-tight">{event ? t("edit_event") : t("new_event")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={`${t("event_name")} *`} className="sm:col-span-2">
            <Input data-testid="event-name-input" required value={form.name} onChange={set("name")} className={fieldInputCls} />
          </Field>
          <Field label={`${t("track")} *`}>
            <Input data-testid="event-track-input" required list="event-tracks" value={form.track} onChange={set("track")} className={fieldInputCls} />
            <datalist id="event-tracks">{tracks.map((tr) => <option key={tr} value={tr} />)}</datalist>
          </Field>
          <Field label={`${t("event_type")} *`}>
            <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
              <SelectTrigger data-testid="event-type-select" className={fieldInputCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {typeOptions.map((ty) => <SelectItem key={ty} value={ty} data-testid={`event-type-option-${ty}`}>{t(`type_${ty}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {form.event_type === "NATIONAL" && (
            <Field label={t("country")}>
              <Input data-testid="event-country-input" value={form.country} onChange={set("country")} className={fieldInputCls} />
            </Field>
          )}
          {!settings.hide_categories && (
            <Field label={`${t("categories")} · ${t("category_hint")}`} className="sm:col-span-2">
              <TagInput testid="event-categories" values={form.categories} suggestions={meta.categories} onChange={(v) => setForm({ ...form, categories: v })} />
            </Field>
          )}
          <Field label={t("drivers")} className="sm:col-span-2">
            <TagInput testid="event-drivers" values={form.drivers} suggestions={[...new Set([...registry.map(driverName), ...meta.drivers])]} onChange={(v) => setForm({ ...form, drivers: v })} />
            {suggestedDrivers.length > 0 && (
              <div data-testid="event-registry-drivers" className="pt-1 space-y-1.5">
                <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{t("registry_drivers")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestedDrivers.map((n) => (
                    <button type="button" key={n} data-testid={`event-registry-driver-${n}`} onClick={() => setForm((f) => ({ ...f, drivers: [...f.drivers, n] }))}
                      className="font-mono text-xs border border-dashed border-[#FF3B30]/50 text-slate-300 rounded-sm px-2 py-0.5 hover:bg-[#FF3B30]/10 hover:text-white transition-colors duration-200">
                      + {n}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Field>
          <Field label={`${t("start_date")} *`}>
            <Input data-testid="event-start-date-input" type="date" required value={form.start_date} onChange={set("start_date")} className={fieldInputCls} />
          </Field>
          <Field label={t("end_date")}>
            <Input data-testid="event-end-date-input" type="date" min={form.start_date} value={form.end_date} onChange={set("end_date")} className={fieldInputCls} />
          </Field>
          <Field label={t("notes")} className="sm:col-span-2">
            <Textarea data-testid="event-notes-input" rows={3} value={form.notes} onChange={set("notes")} className={`${fieldInputCls} h-auto font-sans`} />
          </Field>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" data-testid="event-form-cancel-btn" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
            <Button type="submit" disabled={busy} data-testid="event-form-save-btn" className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-base px-6">{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
